import { useMemo, useState } from "react";
import { MDBIcon } from "mdbreact";
import { STATUS } from "./status";

const FILTERS = [
  { key: "", label: "All" },
  { key: "notSet", label: "Not set" },
  { key: "brandAvailable", label: "Brand available" },
  { key: "needsBrand", label: "Needs brand" },
  { key: "ready", label: "Ready" },
];

/**
 * Services of the department grouped by section, with their tagging status.
 * `statusOf(id)` returns a key of STATUS.
 */
export default function ServiceList({
  services,
  selectedId,
  statusOf,
  onSelect,
}) {
  const [search, setSearch] = useState(""),
    [filter, setFilter] = useState("");

  const groups = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return services
      .filter(
        ({ name = "", abbreviation = "" }) =>
          !keyword ||
          name.toLowerCase().includes(keyword) ||
          abbreviation.toLowerCase().includes(keyword),
      )
      .filter(({ id }) => !filter || statusOf(id) === filter)
      .reduce((result, service) => {
        (result[service.section] = result[service.section] || []).push(service);
        return result;
      }, {});
  }, [services, search, filter, statusOf]);

  const sections = Object.entries(groups);

  return (
    <div
      className="border rounded h-100 d-flex flex-column"
      style={{ minHeight: 0 }}
    >
      <div className="p-2 border-bottom">
        <div className="position-relative mb-2">
          <MDBIcon
            icon="search"
            className="position-absolute text-muted"
            style={{ top: "50%", left: 10, transform: "translateY(-50%)" }}
          />
          <input
            className="form-control form-control-sm"
            style={{ paddingLeft: 30 }}
            placeholder="Search service (e.g. CBC)"
            value={search}
            onChange={({ target }) => setSearch(target.value)}
          />
        </div>
        <div className="d-flex flex-wrap" style={{ gap: 4 }}>
          {FILTERS.map(({ key, label }) => (
            <button
              key={key || "all"}
              type="button"
              className={`btn btn-sm m-0 px-2 py-1 z-depth-0 ${
                filter === key ? "btn-primary" : "btn-outline-primary"
              }`}
              style={{ textTransform: "none" }}
              onClick={() => setFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ overflowY: "auto", maxHeight: "65vh" }}>
        {!sections.length && (
          <p className="text-center text-muted small my-4">
            No services found.
          </p>
        )}
        {sections.map(([section, items]) => (
          <div key={section}>
            <div
              className="px-2 py-1 small font-weight-bold text-uppercase text-muted grey lighten-4"
              style={{ position: "sticky", top: 0, zIndex: 1 }}
            >
              {section}
            </div>
            {items.map((service) => {
              const { id, name, abbreviation, specimen } = service;
              const status = STATUS[statusOf(id)];
              const isActive = selectedId === id;
              return (
                <div
                  key={id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect(service)}
                  onKeyDown={(e) => e.key === "Enter" && onSelect(service)}
                  className={`px-2 py-2 border-bottom d-flex align-items-center ${
                    isActive ? "blue lighten-5" : ""
                  }`}
                  style={{
                    cursor: "pointer",
                    borderLeft: `3px solid ${isActive ? "#2196f3" : "transparent"}`,
                  }}
                >
                  <MDBIcon
                    icon={status.icon}
                    className="mr-2"
                    style={{ color: status.color }}
                    title={status.hint}
                  />
                  <div className="text-truncate" style={{ minWidth: 0 }}>
                    <div className="text-truncate">
                      {abbreviation && abbreviation !== name ? (
                        <>
                          <b>{abbreviation}</b>
                          <small className="text-muted"> · {name}</small>
                        </>
                      ) : (
                        <b>{name}</b>
                      )}
                    </div>
                    {specimen && (
                      <small className="text-muted d-block text-truncate">
                        {specimen}
                      </small>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
